// Operatörün kendi aktif bürünme oturumu varken her ekranda duran bant
// (çizim: panel/Shell.jsx InternalTopBar `impersonating`). Kaynak
// GET /ops/v1/impersonations/active'in varsayılan kapsamıdır: sunucu bunu
// çağıranın KENDİ oturumlarıyla sınırlar, yani admin başkasının oturumunu
// burada görmez. Sorgu anahtarı oturumlar sayfasının kendi-kapsam anahtarıyla
// aynıdır; başlatma/bitirme ikisini birlikte tazeler.
//
// Çizimdeki "salt-okunur" ve geri sayım metni yok: sunucu yazma kapsamını
// döndürmüyor ve bitiş saati zaten bir an (ends_at), sayaç değil.

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { UserCog, UserX } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";

import { usePermission } from "@/hooks/usePermission";
import {
  activeImpersonationsQueryKey,
  fetchActiveImpersonations,
} from "@/lib/impersonation-queries";
import { cn } from "@/lib/utils";

import { EndImpersonationModal } from "./EndImpersonationModal";
import type { ImpersonationSession } from "./StartImpersonationModal";

const POLL_MS = 60_000;

export function ImpersonationBand(): React.ReactElement | null {
  const { t, i18n } = useTranslation();
  const canRead = usePermission("impersonate.read");
  const [endTarget, setEndTarget] = useState<ImpersonationSession | null>(null);

  const query = useQuery({
    queryKey: activeImpersonationsQueryKey(false),
    queryFn: () => fetchActiveImpersonations({}),
    enabled: canRead,
    // Sunucu süresi geçen oturumu listeden düşürür; yoklama bandı o anla hizalar.
    refetchInterval: POLL_MS,
  });

  const sessions = (query.data?.sessions ?? []).filter((s) => s.state === "active");
  const first = sessions[0];
  if (!canRead || first === undefined) {
    return endTarget === null ? null : (
      <EndImpersonationModal session={endTarget} onClose={(): void => setEndTarget(null)} />
    );
  }

  const endsAt = new Intl.DateTimeFormat(i18n.language, { timeStyle: "short" }).format(
    new Date(first.ends_at),
  );

  return (
    <>
      <div
        role="status"
        className={cn(
          "flex flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-1.5 md:px-7",
          "bg-[var(--state-danger)] font-sans text-[12px] font-medium text-[var(--fg-on-accent)]",
        )}
      >
        <UserCog size={14} aria-hidden="true" className="shrink-0" />
        <span className="min-w-0 flex-1">
          {sessions.length === 1
            ? t("impersonation_band.single", {
                tenant: first.target_tenant_name ?? first.target_tenant_id,
                time: endsAt,
              })
            : t("impersonation_band.multiple", { count: sessions.length })}
        </span>
        {sessions.length === 1 ? (
          <button
            type="button"
            onClick={(): void => setEndTarget(first)}
            className={cn(
              "inline-flex items-center gap-1 rounded-[4px] border border-current px-2.5 py-0.5",
              "text-[11px] font-semibold hover:underline",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus-ring)]",
            )}
          >
            <UserX size={12} aria-hidden="true" />
            {t("impersonation_band.end")}
          </button>
        ) : (
          <RouterLink
            to="/impersonations"
            className="rounded-[4px] border border-current px-2.5 py-0.5 text-[11px] font-semibold text-[var(--fg-on-accent)] no-underline hover:underline"
          >
            {t("impersonation_band.manage")}
          </RouterLink>
        )}
      </div>
      {endTarget !== null && (
        <EndImpersonationModal session={endTarget} onClose={(): void => setEndTarget(null)} />
      )}
    </>
  );
}
